import { XplanFiler } from './XplanFiler';
import axios from 'axios';
import { APICalls, getPayload, getXplanURL } from './helper';
import { LogError } from '../actions/errorWorker';
import { existingDocNoteScreen, entityCriteriaScreen, successScreen } from './navigationWorker';
import { handleResponseData } from './helper';
import { getQueuedEmails, clearQueue } from './queueWorker';

export async function fileEmail(data: any) {
    const filingMode = sessionStorage.getItem('filing_mode');
    console.log("fileEmail called - filing mode:", filingMode);
    
    // Check if we're filing a queue
    if (filingMode === 'queue') {
        const queuedEmails = getQueuedEmails();
        console.log(`Batch filing ${queuedEmails.length} emails`);
        
        if (queuedEmails.length === 0) {
            throw new Error('No emails in queue to file');
        }
        
        // Determine URL and method based on file type
        const isNote = data.fileToType === 'note';
        const url = getXplanURL() + (isNote ? APICalls.FileEmail : APICalls.FileEmailtoTask);
        const filer = new XplanFiler(url);
        
        console.log("Starting batch filing operation...");
        let result;
        
        // File all queued emails with the same options based on type
        if (data.fileToType === 'note') {
            result = await filer.fileQueueToXplan(data, queuedEmails);
        } else if (data.fileToType === 'newtask') {
            result = await filer.fileQueueToNewTasks(data, queuedEmails);
        } else if (data.fileToType === 'activetask') {
            result = await filer.fileQueueToActiveTasks(data, queuedEmails);
        } else {
            throw new Error(`Unsupported file type for batch filing: ${data.fileToType}`);
        }
        
        console.log("Batch filing complete:", result);
        
        // Clear the filing mode
        sessionStorage.removeItem('filing_mode');
        
        // Clear the queue on success
        clearQueue();
        
        // Store detailed results in session for success screen
        sessionStorage.setItem('batch_filing_results', JSON.stringify(result));
        
        // Build simple header message - all emails filed to ONE note/task
        const total = result.succeeded.length + result.failed.length;
        const itemType = data.fileToType === 'note' ? 'note' : 'task';
        const linkTitle = data.fileToType === 'note' ? 'Go to Notes' : 'Go to Tasks';
        let message = '';
        if (result.failed.length > 0) {
            message = `Filed ${result.succeeded.length} of ${total} email${total > 1 ? 's' : ''} to one ${itemType}`;
        } else {
            message = `Successfully filed ${result.succeeded.length} email${result.succeeded.length > 1 ? 's' : ''} to one ${itemType}`;
        }
        
        const redirectUrl = sessionStorage.getItem('redirectUrl');
        console.log("Showing success screen");
        successScreen(false, redirectUrl, linkTitle, message);
        
        return;
    }
    
    console.log("Normal single email filing");
    // Normal single email filing
    const isNote = data.fileToType === 'note';
    const isFiledNote = data.fileToType === 'existingnote';
    
    if (isFiledNote) {
        // Handle filing to existing note (PATCH)
        const noteId = data.note_id;
        if (!noteId) throw new Error("Note ID is required for filing to existing note");
        
        const url = getXplanURL() + APICalls.FileEmail;
        const filer = new XplanFiler(url);
        return filer.fileNoteToXplan(data, noteId);
    }

    const url = getXplanURL() + (isNote ? APICalls.FileEmail : APICalls.FileEmailtoTask);
    const filer = new XplanFiler(url);
    return isNote ? filer.fileNoteToXplan(data) : filer.fileEmailToXplan(data);
}

export async function fileExistingItem(DocId: string, otherData: any) {
    const filingMode = sessionStorage.getItem('filing_mode');
    console.log("fileExistingItem called - filing mode:", filingMode);
    
    // Check if we're filing a queue
    if (filingMode === 'queue') {
        const queuedEmails = getQueuedEmails();
        console.log(`Batch filing ${queuedEmails.length} emails to existing note`);
        
        if (queuedEmails.length === 0) {
            throw new Error('No emails in queue to file');
        }
        
        const url = getXplanURL() + APICalls.FileEmail;
        const filer = new XplanFiler(url);
        
        console.log("Starting batch filing to existing note...");
        
        // File all queued emails as attachments to the existing note
        const result = await filer.fileQueueToExistingNote(DocId, otherData, queuedEmails);
        
        console.log("Batch filing to existing note complete:", result);
        
        // Clear the filing mode
        sessionStorage.removeItem('filing_mode');
        
        // Clear the queue on success
        clearQueue();
        
        // Store detailed results in session for success screen
        sessionStorage.setItem('batch_filing_results', JSON.stringify(result));
        
        // Build simple header message
        const total = result.succeeded.length + result.failed.length;
        let message = '';
        if (result.failed.length > 0) {
            message = `Filed ${result.succeeded.length} of ${total} email${total > 1 ? 's' : ''} to existing note`;
        } else {
            message = `Successfully filed ${result.succeeded.length} email${result.succeeded.length > 1 ? 's' : ''} to existing note`;
        }
        
        const redirectUrl = sessionStorage.getItem('redirectUrl');
        console.log("Showing success screen");
        successScreen(false, redirectUrl, 'Go to Notes', message);
        
        return;
    }
    
    console.log("Normal single email filing to existing note");
    // Normal single email filing
    const url = getXplanURL() + APICalls.FileEmail;
    const filer = new XplanFiler(url);
    return filer.fileExistingNoteToXplan(DocId, otherData);
}

export function GetEmailIdentifiers() {
    if (Office?.context?.mailbox?.item) {
        return '?ext_type=OutlookAddInV2&ext_item_id=' + encodeURIComponent(Office.context.mailbox.item['conversationId']);
    }
    return null;
}

export async function FindDocNotes() {
    const ei = GetEmailIdentifiers();
    if (!ei) {
        sessionStorage.setItem('hasDocNote', '0');
        entityCriteriaScreen(false);
        return;
    }

    const errorMessage = 'Failed to retrieve email history.';
    const payload = getPayload(getXplanURL() + APICalls.ExistingDocNote + ei, null, 'GET');

    try {
        
        const response = await axios.post(APICalls.AWSUrl(), payload);
        // Pass suppress_error as true to prevent error dialogs when no history is found
        const data = handleResponseData(response, errorMessage, true);
        // Skip existing notes screen if data is empty or null
        if (data && Array.isArray(data) && data.length > 0) {
            sessionStorage.setItem('hasDocNote', '1');
            existingDocNoteScreen(data);
        } else {
            sessionStorage.setItem('hasDocNote', '0');
            entityCriteriaScreen(false);
        }
    } catch (error) {
        // Silently continue to entity criteria screen if no history found
        // Only log errors that are not related to empty history
        if (error.message && !error.message.includes(errorMessage)) {
            LogError(error, { payload }, 'FindDocNotes');
        }
        sessionStorage.setItem('hasDocNote', '0');
        entityCriteriaScreen(false);
    };
}
